import { useEffect, useState } from 'react';
import type { ProposalBuildEvent, ProposalBuildState } from '@shared/api';
import { getProposalBuild } from '../api/client';

export type BuildConnectionStatus = 'connected' | 'reconnecting' | 'disconnected';

export interface ProposalBuildStore {
  state: ProposalBuildState | null;
  loading: boolean;
  error: string | null;
  connectionStatus: BuildConnectionStatus;
}

function parseEvent(value: string): ProposalBuildEvent | null {
  try {
    const parsed = JSON.parse(value) as Partial<ProposalBuildEvent>;
    return parsed.kind === 'build_state' && parsed.state ? (parsed as ProposalBuildEvent) : null;
  } catch {
    return null;
  }
}

function isNewerSnapshot(current: ProposalBuildState | null, next: ProposalBuildState): boolean {
  if (!current) return true;
  if (current.runId !== next.runId) return next.attempt >= current.attempt;
  return next.sequence > current.sequence;
}

/**
 * Loads the server-owned build snapshot, then follows it through an SSE
 * subscription. Native EventSource retries dropped connections; each event
 * is authoritative, so reconciliation never restarts generation.
 */
export function useProposalBuild(proposalId: string | null): ProposalBuildStore {
  const [state, setState] = useState<ProposalBuildState | null>(null);
  const [loading, setLoading] = useState(proposalId !== null);
  const [error, setError] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] =
    useState<BuildConnectionStatus>('disconnected');

  useEffect(() => {
    if (!proposalId) {
      setState(null);
      setLoading(false);
      setConnectionStatus('disconnected');
      return;
    }

    let live = true;
    setLoading(true);
    setError(null);
    let source: EventSource | null = null;
    void getProposalBuild(proposalId)
      .then((next) => {
        if (live) setState((current) => isNewerSnapshot(current, next) ? next : current);
        if (!live || next.mode === 'simulated') return;
        source = new EventSource(`/api/proposals/${encodeURIComponent(proposalId)}/build/events`);
        source.onopen = () => {
          if (!live) return;
          setConnectionStatus('connected');
          setError(null);
        };
        source.onmessage = (event) => {
          if (!live) return;
          const eventState = parseEvent(event.data);
          if (eventState) setState((current) => isNewerSnapshot(current, eventState.state) ? eventState.state : current);
        };
        source.onerror = () => { if (live) setConnectionStatus('reconnecting'); };
      })
      .catch((cause) => {
        if (live) setError(cause instanceof Error ? cause.message : String(cause));
      })
      .finally(() => {
        if (live) setLoading(false);
      });

    return () => {
      live = false;
      source?.close();
      setConnectionStatus('disconnected');
    };
  }, [proposalId]);

  return { state, loading, error, connectionStatus };
}
